import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";

import { fetchWithAuth } from "@/lib/utils";

export type ByokValidationStatus = "active" | "invalid" | "unverified";
type ByokScopeValidationStatus = ByokValidationStatus | "unknown";

export interface ByokProviderCapability {
  provider: string;
  label: string;
  models: string[];
}

export interface ByokScopeState {
  configured: boolean;
  supported?: boolean;
  validation_status?: ByokScopeValidationStatus;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ByokProviderState extends ByokProviderCapability {
  user: ByokScopeState;
  organization: ByokScopeState;
  /** Direct server environment fallback, normalized for older servers. */
  environment: ByokScopeState;
}

interface ByokProviderStatus {
  provider: string;
  user: ByokScopeState;
  organization: ByokScopeState;
  environment: ByokScopeState;
}

type ByokAvailability = "loading" | "supported" | "unsupported" | "error";

export interface UseByokKeysReturn {
  availability: ByokAvailability;
  providers: ByokProviderState[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  save: (provider: string, value: string) => Promise<boolean>;
  remove: (provider: string) => Promise<boolean>;
  validate: (provider: string) => Promise<ByokValidationStatus | null>;
}

const unsupportedStatus = new Set([404, 405, 501]);

async function responseError(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object") {
      const record = body as Record<string, unknown>;
      for (const key of ["detail", "error", "message"]) {
        if (typeof record[key] === "string") return record[key] as string;
      }
    }
  } catch {
    // Preserve the status fallback when an older server returns HTML/plain text.
  }
  return `HTTP ${response.status}: ${response.statusText}`;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function asCapabilities(value: unknown): ByokProviderCapability[] | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const providers = (value as Record<string, unknown>).providers;
  if (!Array.isArray(providers)) return null;
  if (!providers.every((entry) => (
    entry && typeof entry === "object" && !Array.isArray(entry)
    && typeof (entry as Record<string, unknown>).provider === "string"
    && typeof (entry as Record<string, unknown>).label === "string"
    && isStringArray((entry as Record<string, unknown>).models)
  ))) return null;
  return providers as ByokProviderCapability[];
}

function inlineCapabilities(value: unknown): ByokProviderCapability[] | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return asCapabilities((value as Record<string, unknown>).capabilities);
}

function asScopeState(value: unknown): ByokScopeState {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { configured: false };
  }

  const record = value as Record<string, unknown>;
  const state: ByokScopeState = { configured: record.configured === true };
  if (typeof record.supported === "boolean") state.supported = record.supported;
  if (typeof record.created_at === "string" || record.created_at === null) {
    state.created_at = record.created_at;
  }
  if (typeof record.updated_at === "string" || record.updated_at === null) {
    state.updated_at = record.updated_at;
  }

  if (record.validation_status !== undefined) {
    state.validation_status = record.validation_status === "active"
      || record.validation_status === "invalid"
      || record.validation_status === "unverified"
      ? record.validation_status
      : "unknown";
  }
  return state;
}

function asStates(value: unknown): ByokProviderStatus[] | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const providers = (value as Record<string, unknown>).providers;
  if (!Array.isArray(providers)) return null;
  return providers.flatMap((entry): ByokProviderStatus[] => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const record = entry as Record<string, unknown>;
    if (typeof record.provider !== "string") return [];
    return [{
      provider: record.provider,
      user: asScopeState(record.user),
      organization: asScopeState(record.organization),
      // Older servers do not send this additive scope.  Treat its absence as
      // not configured so independently deployed UI/server versions remain
      // usable rather than rendering a blank or throwing in the panel.
      environment: asScopeState(record.environment),
    }];
  });
}

/**
 * New servers provide status and capabilities together. Older agno-server
 * versions fall back to their separate capabilities route; a 404 remains an
 * explicit compatibility state rather than an empty provider list or error.
 */
export function useByokKeys(getToken: () => Promise<string>): UseByokKeysReturn {
  const { t } = useTranslation("connections");
  const [availability, setAvailability] = useState<ByokAvailability>("loading");
  const [providers, setProviders] = useState<ByokProviderState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const stateResponse = await fetchWithAuth(
        "/api/user-secrets/byok",
        { headers: {} },
        getToken,
      );
      if (unsupportedStatus.has(stateResponse.status)) {
        setAvailability("unsupported");
        setProviders([]);
        return;
      }
      if (!stateResponse.ok) throw new Error(await responseError(stateResponse));
      const statePayload: unknown = await stateResponse.json();
      const states = asStates(statePayload);
      if (!states) throw new Error(t("byok.errors.invalidStatusResponse"));

      // Newer servers include the stable catalog as an additive field in the
      // status response, avoiding the old serial capabilities round-trip.
      // Older servers remain supported through the original endpoint.
      let capabilities = inlineCapabilities(statePayload);
      if (!capabilities) {
        const capabilityResponse = await fetchWithAuth(
          "/api/user-secrets/byok/capabilities",
          { headers: {} },
          getToken,
        );
        if (unsupportedStatus.has(capabilityResponse.status)) {
          setAvailability("unsupported");
          setProviders([]);
          return;
        }
        if (!capabilityResponse.ok) throw new Error(await responseError(capabilityResponse));
        capabilities = asCapabilities(await capabilityResponse.json());
        if (!capabilities) throw new Error(t("byok.errors.invalidCapabilityResponse"));
      }

      // Capabilities remain the canonical catalog.  Normalize status rows so
      // additive server fields, unknown future states, and absent legacy
      // scopes cannot break independently deployed UI/server versions.
      const byProvider = new Map(states.map((item) => [item.provider, item]));
      setProviders(capabilities.map((capability) => {
        const state = byProvider.get(capability.provider);
        return {
          ...capability,
          user: state?.user ?? { configured: false },
          organization: state?.organization ?? { configured: false },
          environment: state?.environment ?? { configured: false },
        };
      }));
      setAvailability("supported");
    } catch (cause) {
      setAvailability("error");
      setProviders([]);
      setError(cause instanceof Error ? cause.message : t("byok.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [getToken, t]);

  const save = useCallback(async (provider: string, value: string): Promise<boolean> => {
    setError(null);
    try {
      const response = await fetchWithAuth(
        "/api/user-secrets/byok",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ provider, value }),
        },
        getToken,
      );
      if (!response.ok) throw new Error(await responseError(response));
      await refresh();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("byok.errors.save"));
      return false;
    }
  }, [getToken, refresh, t]);

  const remove = useCallback(async (provider: string): Promise<boolean> => {
    setError(null);
    try {
      const response = await fetchWithAuth(
        `/api/user-secrets/byok/${encodeURIComponent(provider)}`,
        { method: "DELETE", headers: {} },
        getToken,
      );
      if (!response.ok) throw new Error(await responseError(response));
      await refresh();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("byok.errors.remove"));
      return false;
    }
  }, [getToken, refresh, t]);

  const validate = useCallback(async (provider: string): Promise<ByokValidationStatus | null> => {
    setError(null);
    try {
      const response = await fetchWithAuth(
        `/api/user-secrets/byok/${encodeURIComponent(provider)}/validate`,
        { method: "POST", headers: {} },
        getToken,
      );
      if (!response.ok) throw new Error(await responseError(response));
      const body: unknown = await response.json();
      const status = body && typeof body === "object"
        ? (body as Record<string, unknown>).validation_status
        : null;
      if (status !== "active" && status !== "invalid" && status !== "unverified") {
        throw new Error(t("byok.errors.invalidValidationResponse"));
      }
      await refresh();
      return status;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("byok.errors.validate"));
      return null;
    }
  }, [getToken, refresh, t]);

  useRefreshOnReconnect(refresh);

  useEffect(() => { void refresh(); }, [refresh]);

  return { availability, providers, loading, error, refresh, save, remove, validate };
}
